import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

import PresentationSlide10 from '../PresentationSlide10';
import PresentationSlide11 from '../PresentationSlide11';
import PresentationSlide12 from '../PresentationSlide12';
import RivieraSlideSimulador from './RivieraSlideSimulador';
import RivieraSlidePlataforma from './RivieraSlidePlataforma';
import RivieraSlidePreco from './RivieraSlidePreco';
import RivieraSlideNutricao from './RivieraSlideNutricao';

const VALID_SLIDES = [10, 11, 12, 13, 14, 15, 16];

const PresentationContainer = styled.div`
  width: 100%;
  height: 100vh;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const SlideContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
  height: 100%;
  overflow: hidden;
`;

const AnimatedSlideContainer = styled(motion.div)`
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const slideVariants = {
  enterFromRight: {
    x: "100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: { type: "tween", duration: 0.25, ease: "easeOut" }
  },
  enterFromLeft: {
    x: "-100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: { type: "tween", duration: 0.25, ease: "easeOut" }
  },
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    filter: "blur(0px)",
    transition: { type: "tween", duration: 0.25, ease: "easeOut" }
  },
  exitToLeft: {
    x: "-100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: { type: "tween", duration: 0.25, ease: "easeIn" }
  },
  exitToRight: {
    x: "100%",
    opacity: 0,
    filter: "blur(5px)",
    transition: { type: "tween", duration: 0.25, ease: "easeIn" }
  }
};

const glitchAnimation = {
  hidden: {
    clipPath: "inset(100% 0 0 0)",
  },
  visible: {
    clipPath: "inset(0 0 0 0)",
    transition: { duration: 0.25, ease: "easeOut" }
  },
  exit: {
    clipPath: "inset(0 0 100% 0)",
    transition: { duration: 0.20, ease: "easeIn" }
  }
};

const RivieraPresentationPage = () => {
  const { slideNumber } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const baseRoute = '/riviera/apresentacao';

  const parsedSlide = slideNumber !== undefined ? parseInt(slideNumber) : 10;
  const [currentSlide, setCurrentSlide] = useState(() => {
    const idx = VALID_SLIDES.indexOf(parsedSlide);
    return idx !== -1 ? idx : 0;
  });
  const [direction, setDirection] = useState("forward");
  const noAnim = location.state?.noAnim;
  const [hasInitialized, setHasInitialized] = useState(false);
  const totalSlides = VALID_SLIDES.length;

  useEffect(() => {
    if (noAnim) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [noAnim, navigate, location.pathname]);

  useEffect(() => {
    if (slideNumber) {
      const slideNum = parseInt(slideNumber);
      const newIndex = VALID_SLIDES.indexOf(slideNum);

      if (newIndex !== -1) {
        if (hasInitialized) {
          if (newIndex > currentSlide) {
            setDirection("forward");
          } else if (newIndex < currentSlide) {
            setDirection("backward");
          }
        }
        setCurrentSlide(newIndex);
      } else {
        navigate(`${baseRoute}/10`, { replace: true });
      }
    } else {
      navigate(`${baseRoute}/10`, { replace: true });
    }

    if (!hasInitialized) {
      setHasInitialized(true);
    }
  }, [slideNumber, navigate, currentSlide, hasInitialized, totalSlides, baseRoute]);

  const handleRightArrow = useCallback(() => {
    const currentParam = parseInt(slideNumber, 10);
    const idx = VALID_SLIDES.indexOf(currentParam);
    if (idx !== -1 && idx < VALID_SLIDES.length - 1) {
      setDirection("forward");
      navigate(`${baseRoute}/${VALID_SLIDES[idx + 1]}`);
    }
  }, [slideNumber, navigate, baseRoute]);

  const handleLeftArrow = useCallback(() => {
    const currentParam = parseInt(slideNumber, 10);
    const idx = VALID_SLIDES.indexOf(currentParam);
    if (idx > 0) {
      setDirection("backward");
      navigate(`${baseRoute}/${VALID_SLIDES[idx - 1]}`);
    }
  }, [slideNumber, navigate, baseRoute]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        handleRightArrow();
      } else if (e.key === 'ArrowLeft') {
        handleLeftArrow();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleRightArrow, handleLeftArrow]);

  const renderCurrentSlide = () => {
    const currentParam = VALID_SLIDES[currentSlide] ?? 10;
    switch (currentParam) {
      case 10:
        return <PresentationSlide10 />;
      case 11:
        return <PresentationSlide11 />;
      case 12:
        return <PresentationSlide12 />;
      case 13:
        return <RivieraSlideSimulador />;
      case 14:
        return <RivieraSlidePlataforma />;
      case 15:
        return <RivieraSlidePreco />;
      case 16:
        return <RivieraSlideNutricao />;
      default:
        return null;
    }
  };

  return (
    <PresentationContainer>
      <SlideContainer>
        {noAnim ? (
          <div style={{ width: '100%', height: '100%' }}>
            {renderCurrentSlide()}
          </div>
        ) : (
          <AnimatePresence mode="wait" initial={false}>
            <AnimatedSlideContainer
              key={currentSlide}
              initial={noAnim ? false : (currentSlide === 0 && !hasInitialized ? "center" : (direction === "forward" ? "enterFromRight" : "enterFromLeft"))}
              animate="center"
              exit={noAnim ? undefined : (direction === "forward" ? "exitToLeft" : "exitToRight")}
              variants={noAnim ? {} : slideVariants}
              transition={noAnim ? { duration: 0 } : undefined}
            >
              <motion.div
                style={{ width: '100%', height: '100%' }}
                initial={noAnim ? "visible" : (currentSlide === 0 && !hasInitialized ? "visible" : "hidden")}
                animate="visible"
                exit={noAnim ? undefined : "exit"}
                variants={noAnim ? {} : glitchAnimation}
              >
                {renderCurrentSlide()}
              </motion.div>
            </AnimatedSlideContainer>
          </AnimatePresence>
        )}
      </SlideContainer>
    </PresentationContainer>
  );
};

export default RivieraPresentationPage;
